import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Table,
  Alert,
  Form,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import FileAuditUploadService from "../../../../services/fileAuditUpload.service";
import { FileAudit as UploadedFile } from "../../../../models/FileAudit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faTrashAlt, faEye } from "@fortawesome/free-solid-svg-icons";
import "./style.css";

interface FileUploadProps {
  auditQuestionId: number;
  onUploadSuccess?: () => void;
}

const FileUpload = ({ auditQuestionId, onUploadSuccess }: FileUploadProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string>("");
  const [loadingUpload, setLoadingUpload] = useState<boolean>(false);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    loadFiles();
  }, [auditQuestionId]);

  const loadFiles = async () => {
    setLoadingFiles(true);
    setError("");
    try {
      const data = await FileAuditUploadService.getFilesQuestion(
        auditQuestionId
      );
      setFiles(data);
    } catch (err: any) {
      console.error("Error loading files:", err);
      setError(err.message || "Error al cargar archivos adjuntos.");
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      handleUpload(droppedFiles);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
    }
  };

  const handleUpload = async (filesToUpload: FileList) => {
    if (!filesToUpload || filesToUpload.length === 0) return;

    setLoadingUpload(true);
    setError("");

    try {
      const uploadedFiles = await FileAuditUploadService.uploadFilesQuestion(
        auditQuestionId,
        filesToUpload
      );
      setFiles((prev) => [...uploadedFiles, ...prev]);
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err: any) {
      console.error("Error uploading files:", err);
      setError(
        err.message || "Error al subir los archivos. Inténtalo de nuevo."
      );
    } finally {
      setLoadingUpload(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (fileId: number) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este archivo?")) {
      return;
    }
    setDeletingId(fileId);
    setError("");
    try {
      await FileAuditUploadService.deleteFile(fileId);
      setFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
    } catch (err: any) {
      console.error("Error deleting file:", err);
      setError(
        err.message || "Error al eliminar el archivo. Inténtalo de nuevo."
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="file-upload-container mt-2">
      <style>{`
        .drop-zone {
          border: 2px dashed #ccc;
          border-radius: 8px;
          padding: 2rem;
          text-align: center;
          color: #aaa;
          transition: all 0.3s ease;
        }
        .drop-zone.dragging {
          border-color: #0d6efd;
          background-color: #e7f1ff;
          color: #0d6efd;
        }
        .drop-zone-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }
      `}</style>

      <div
        className={`drop-zone ${isDragging ? "dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="drop-zone-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M8 0a5.53 5.53 0 0 0-3.594 1.342c-.766.66-1.321 1.52-1.464 2.383C1.266 4.095 0 5.555 0 7.318 0 9.366 1.708 11 3.781 11H7.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707V11h4.188C14.502 11 16 9.57 16 7.773c0-1.636-1.242-2.969-2.834-3.194C12.923 1.99 10.69 0 8 0zm-.5 14.5V11h1v3.5a.5.5 0 0 1-1 0z"
            />
          </svg>
        </div>
        <h5>Arrastra y suelta archivos aquí</h5>
        <p className="text-muted">o</p>
        <Form.Group
          controlId={`formFile-${auditQuestionId}`}
          className="mb-3 d-flex justify-content-center"
        >
          <Form.Control
            type="file"
            multiple
            onChange={handleFileChange}
            disabled={loadingUpload}
            ref={fileInputRef}
            className="d-none"
          />
          <Button
            variant="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={loadingUpload}
          >
            <FontAwesomeIcon icon={faUpload} />
            <span className="ms-2">
              {loadingUpload ? "Subiendo..." : "Seleccionar archivos"}
            </span>
          </Button>
        </Form.Group>
      </div>

      {error && (
        <Alert
          variant="danger"
          className="mt-3"
          dismissible
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <div className="mt-4">
        <hr />
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th className="text-center">Nombre del Archivo</th>
                <th className="text-center">Fecha de Subida</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loadingFiles && (
                <tr>
                  <td colSpan={3} className="text-center p-3">
                    <Spinner animation="border" size="sm" role="status" />
                    <span className="ms-2">Cargando archivos...</span>
                  </td>
                </tr>
              )}
              {!loadingFiles && files.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-muted">
                    No hay archivos adjuntos
                  </td>
                </tr>
              )}
              {!loadingFiles &&
                files.map((file) => (
                  <tr key={file.id} style={{ verticalAlign: "middle" }}>
                    <td style={{ wordBreak: "break-all" }}>
                      {file.originalFileName}
                    </td>
                    <td className="text-center">
                      {new Date(file.uploadDate).toLocaleDateString()}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="primary"
                        href={file.shareableUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        className="me-2"
                        title="Ver archivo"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(file.id)}
                        disabled={deletingId === file.id}
                        title="Eliminar archivo"
                      >
                        {deletingId === file.id ? (
                          <Spinner as="span" animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faTrashAlt} />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
